import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Coins } from 'lucide-react';
import { format } from 'date-fns';
import { useState } from 'react';
import { GrantCreditsDialog } from '../GrantCreditsDialog';
import type { UserDetail } from '../types';
import { formatActionLabel, formatReason } from '@/lib/actionLabels';
import { cn } from '@/lib/utils';

export function CreditsPanel({ detail, onChange }: { detail: UserDetail; onChange: () => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-5">
      {/* Balance hero */}
      <div className="rounded-xl border border-border/60 bg-gradient-to-br from-primary/5 to-card p-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/15 flex items-center justify-center">
            <Coins className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
              Current balance
            </div>
            <div className="text-3xl font-semibold tabular-nums">
              {detail.subscription?.credits_remaining ?? '—'}
              <span className="text-sm text-muted-foreground font-normal ml-2">credits</span>
            </div>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Grant / deduct
        </Button>
      </div>

      {/* Transactions */}
      <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
        <div className="p-4 border-b border-border/60">
          <h4 className="text-sm font-semibold">Transaction history</h4>
          <p className="text-xs text-muted-foreground mt-0.5">Latest 200 entries</p>
        </div>
        {detail.credit_transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground py-12 text-center">No transactions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground">
                    Date
                  </th>
                  <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground">
                    Action
                  </th>
                  <th className="text-right px-4 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground">
                    Δ
                  </th>
                  <th className="text-right px-4 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground">
                    Before → After
                  </th>
                  <th className="text-left px-4 py-2.5 font-medium text-xs uppercase tracking-wide text-muted-foreground">
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.credit_transactions.map((tx) => {
                  const meta: any = tx.context_metadata || {};
                  const isPositive = tx.final_credits >= 0;
                  return (
                    <tr key={tx.id} className="border-t border-border/60">
                      <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground text-xs">
                        {format(new Date(tx.created_at), 'dd.MM.yy HH:mm')}
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className="text-xs">
                          {formatActionLabel(tx.action_type)}
                        </Badge>
                      </td>
                      <td
                        className={cn(
                          'px-4 py-2.5 text-right font-mono tabular-nums font-medium',
                          isPositive ? 'text-success' : 'text-destructive'
                        )}
                      >
                        {isPositive ? '+' : ''}
                        {tx.final_credits}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-muted-foreground tabular-nums">
                        {tx.credits_before ?? '—'} → {tx.credits_after ?? '—'}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-xs truncate">
                        {formatReason(meta, tx.action_type, tx.transaction_id)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <GrantCreditsDialog
        open={open}
        onOpenChange={setOpen}
        userId={detail.user.id}
        userEmail={detail.user.email}
        onSuccess={onChange}
      />
    </div>
  );
}
